import { useEffect, useState } from "react";
import { Loader2, Syringe } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, formatRupiah } from "@/lib/api";
import { digitsOnly, gameIdError, waError, newRequestId } from "@/lib/member";
import { useAuth } from "@/context/AuthContext";
import { InjectSummary } from "@/components/admin/InjectSummary";

const GAMES = { royal_dream: "Royal Dream", higgs_domino: "Higgs Domino" };
const EMPTY = { product_id: "royal_dream", game_id: "", wa_number: "", price: "", chip_b: "", bank: "", rek_number: "", rek_name: "", note: "" };

const F = ({ label, error, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-semibold uppercase tracking-widest text-zinc-500">{label}</span>
    {children}
    {error ? <span className="mt-1 block text-[11px] text-rose-400">{error}</span> : null}
  </label>
);

const TypeTabs = ({ value, onChange }) => (
  <div className="inline-flex rounded-xl border border-white/10 p-1" data-testid="inject-type-tabs">
    {[["topup", "Top Up"], ["bongkar", "Bongkar"]].map(([k, l]) => (
      <button key={k} type="button" onClick={() => onChange(k)} data-testid={`inject-type-${k}`}
        className={`rounded-lg px-4 py-2 text-sm transition-colors ${value === k ? "bg-[#e5b80b] font-semibold text-black" : "text-zinc-400 hover:text-white"}`}>
        {l}
      </button>
    ))}
  </div>
);

const BongkarFields = ({ form, set, banks, pricePerB }) => (
  <>
    <F label={`Nominal (B)${form.chip_b ? ` · ${formatRupiah(Number(form.chip_b) * pricePerB)}` : ""}`}>
      <input value={form.chip_b} onChange={(e) => set("chip_b", digitsOnly(e.target.value, 6))} inputMode="numeric" placeholder="Contoh: 50" className="rk-input" data-testid="inject-chip-b" />
    </F>
    <F label="Bank / E-Wallet">
      <select value={form.bank} onChange={(e) => set("bank", e.target.value)} className="rk-input" data-testid="inject-bank">
        <option value="">Pilih bank</option>
        {banks.map((b) => <option key={b} value={b}>{b}</option>)}
      </select>
    </F>
    <F label="Nomor Rekening">
      <input value={form.rek_number} onChange={(e) => set("rek_number", digitsOnly(e.target.value, 30))} inputMode="numeric" className="rk-input" data-testid="inject-rek-number" />
    </F>
    <F label="Nama Rekening">
      <input value={form.rek_name} onChange={(e) => set("rek_name", e.target.value)} className="rk-input" data-testid="inject-rek-name" />
    </F>
  </>
);

export const InjectPanel = () => {
  const { user } = useAuth();
  const [done, setDone] = useState(0);
  return (
    <div className="space-y-6">
      <InjectForm onDone={() => setDone((n) => n + 1)} />
      {user.role === "master" ? <InjectSummary refreshKey={done} /> : null}
    </div>
  );
};

const InjectForm = ({ onDone }) => {
  const [type, setType] = useState("topup");
  const [form, setForm] = useState(EMPTY);
  const [catalog, setCatalog] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [requestId, setRequestId] = useState(newRequestId);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/catalog").then((r) => setCatalog(r.data.packages_by_product));
    api.get("/bongkar/config").then((r) => setCfg(r.data));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const errors = {
    game_id: form.game_id && gameIdError(form.game_id) ? `ID ${gameIdError(form.game_id)}` : "",
    wa_number: form.wa_number && waError(form.wa_number),
  };
  const base = form.game_id && !gameIdError(form.game_id) && !waError(form.wa_number);
  const valid = base && (type === "topup" ? form.price
    : Number(form.chip_b) >= 1 && form.bank && form.rek_number.length >= 4 && form.rek_name.trim().length >= 2);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/inject", {
        type, product_id: form.product_id, game_id: form.game_id, wa_number: form.wa_number, note: form.note,
        client_request_id: requestId,
        ...(type === "topup" ? { price: Number(form.price) } : { chip_b: Number(form.chip_b), bank: form.bank, rek_number: form.rek_number, rek_name: form.rek_name }),
      });
      toast.success(`Inject ${type === "topup" ? "Top Up" : "Bongkar"} berhasil · status SUKSES`);
      setForm({ ...EMPTY, product_id: form.product_id });
      setRequestId(newRequestId());
      onDone();
    } catch (err) {
      toast.error(errMsg(err, "Inject gagal"));
    } finally {
      setBusy(false);
    }
  };

  if (!catalog || !cfg) return <p className="text-sm text-zinc-500">Memuat…</p>;

  return (
    <form onSubmit={submit} className="rk-glass max-w-2xl space-y-5 rounded-2xl p-6" data-testid="inject-form">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <TypeTabs value={type} onChange={setType} />
        <span className="rounded bg-[#e5b80b]/15 px-2 py-1 text-[11px] font-bold text-[#e5b80b]">Langsung SUKSES · tanpa batasan</span>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <F label="Game">
          <select value={form.product_id} onChange={(e) => setForm((f) => ({ ...f, product_id: e.target.value, price: "" }))} className="rk-input" data-testid="inject-game">
            {Object.entries(GAMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </F>
        <F label="ID Pemain" error={errors.game_id}>
          <input value={form.game_id} onChange={(e) => set("game_id", digitsOnly(e.target.value, 10))} inputMode="numeric" placeholder="Maks 10 digit" className="rk-input" data-testid="inject-game-id" />
        </F>
        <F label="Nomor WhatsApp" error={errors.wa_number}>
          <input value={form.wa_number} onChange={(e) => set("wa_number", digitsOnly(e.target.value, 15))} inputMode="tel" placeholder="08xxxxxxxxxx" className="rk-input" data-testid="inject-wa" />
        </F>
        {type === "topup" ? (
          <F label="Paket Top Up">
            <select value={form.price} onChange={(e) => set("price", e.target.value)} className="rk-input" data-testid="inject-package">
              <option value="">Pilih paket</option>
              {catalog[form.product_id].map((p) => <option key={p.price} value={p.price}>{p.label} · {formatRupiah(p.price)}</option>)}
            </select>
          </F>
        ) : (
          <BongkarFields form={form} set={set} banks={cfg.banks} pricePerB={cfg.prices[form.product_id]} />
        )}
      </div>
      <F label="Catatan (opsional)">
        <input value={form.note} onChange={(e) => set("note", e.target.value.slice(0, 200))} placeholder="Alasan inject, dsb." className="rk-input" data-testid="inject-note" />
      </F>
      <button type="submit" disabled={!valid || busy} className="rk-btn-primary inline-flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-50" data-testid="inject-submit">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Syringe size={15} />} Inject Transaksi
      </button>
    </form>
  );
};
